/* Rhody Reviews api/v1 -- Google Business Profile post queue (2026-08-05).
   Runs on the SAME scheduled_posts table as the social queue, filtered to
   platform='google_business'. Unlike the social lane, this one PUBLISHES:
   the GBP API access is approved and needs no new scope. */
import { request } from './_http';

export type GbpPostType = 'standard' | 'event' | 'offer' | 'alert';
export type GbpPostStatus =
  | 'draft'
  | 'scheduled'
  | 'publishing'
  | 'posted'
  | 'failed';

/* Google's callToAction enum. CALL is the odd one out: it takes NO url, and
   Google rejects the post if one is supplied. Every other type requires one. */
export type GbpCtaType =
  | 'BOOK'
  | 'ORDER'
  | 'SHOP'
  | 'LEARN_MORE'
  | 'SIGN_UP'
  | 'CALL';

export const GBP_CTA_LABELS: Record<GbpCtaType, string> = {
  BOOK: 'Book',
  ORDER: 'Order online',
  SHOP: 'Shop',
  LEARN_MORE: 'Learn more',
  SIGN_UP: 'Sign up',
  CALL: 'Call now',
};

export const GBP_POST_TYPE_LABELS: Record<GbpPostType, string> = {
  standard: 'Update',
  event: 'Event',
  offer: 'Offer',
  alert: 'Alert',
};

/* Google caps the post body at 1500 characters. */
export const GBP_CAPTION_MAX = 1500;

/* Extras that ride in the shared `design` jsonb rather than their own columns.
   The publish worker passes these straight through to Google. */
export interface GbpPostDesign {
  photo_urls?: string[];
  event?: {
    title?: string;
    schedule?: Record<string, unknown>;
  };
  offer?: {
    couponCode?: string;
    redeemOnlineUrl?: string;
    termsConditions?: string;
  };
  [k: string]: unknown;
}

export interface GbpPost {
  id: string;
  platform: 'google_business';
  target_location_id: string | null;
  location_label: string | null;
  post_type: GbpPostType;
  caption: string;
  cta_type: GbpCtaType | null;
  cta_url: string | null;
  design: GbpPostDesign;
  scheduled_at: string | null;
  status: GbpPostStatus;
  position: number;
  posted_at: string | null;
  /* Present only once it actually published. Also the duplicate guard: the
     worker refuses any row that already carries one. */
  external_post_id: string | null;
  external_post_url: string | null;
  last_error: string | null;
  created_at: string | null;
  updated_at: string | null;
}

export interface GbpPostsResponse {
  client_id: string;
  count: number;
  posts: GbpPost[];
}

export interface GbpPostInput {
  caption: string;
  post_type?: GbpPostType;
  cta_type?: GbpCtaType | null;
  cta_url?: string | null;
  /* Omit or send '' to save as a draft; send an ISO time to queue it. */
  scheduled_at?: string | null;
  design?: GbpPostDesign;
}
export type GbpPostPatch = Partial<GbpPostInput>;

export interface GbpPostResult {
  client_id: string;
  post: GbpPost;
}

export interface GbpPublishNowResult {
  client_id: string;
  summary: Record<string, unknown>;
  post: GbpPost | null;
}

export const gbpPostsApi = {
  listPosts(
    params: { status?: GbpPostStatus } = {},
    signal?: AbortSignal,
  ): Promise<GbpPostsResponse> {
    return request<GbpPostsResponse>('/api/v1/gbp/posts', {
      query: { status: params.status },
      signal,
    });
  },
  createPost(input: GbpPostInput): Promise<GbpPostResult> {
    return request<GbpPostResult>('/api/v1/gbp/posts', {
      method: 'POST',
      body: input,
    });
  },
  updatePost(id: string, patch: GbpPostPatch): Promise<GbpPostResult> {
    return request<GbpPostResult>(`/api/v1/gbp/posts/${encodeURIComponent(id)}`, {
      method: 'PATCH',
      body: patch,
    });
  },
  deletePost(id: string): Promise<{ client_id: string; deleted: string }> {
    return request<{ client_id: string; deleted: string }>(
      `/api/v1/gbp/posts/${encodeURIComponent(id)}`,
      { method: 'DELETE' },
    );
  },
  publishNow(id: string): Promise<GbpPublishNowResult> {
    return request<GbpPublishNowResult>(
      `/api/v1/gbp/posts/${encodeURIComponent(id)}/publish-now`,
      { method: 'POST' },
    );
  },
};

/* Client-side mirror of the server's validation. The server is the real gate;
   this exists so the owner sees the problem while typing instead of after a
   round trip. Returns a human message, or null when the post is publishable. */
export function validateGbpPost(input: GbpPostInput): string | null {
  const caption = (input.caption || '').trim();
  if (!caption) return 'Write something to post.';
  if (caption.length > GBP_CAPTION_MAX) {
    return `That's ${caption.length} characters. Google's limit is ${GBP_CAPTION_MAX}.`;
  }
  const cta = input.cta_type;
  if (cta) {
    const url = (input.cta_url || '').trim();
    if (cta === 'CALL' && url) {
      return "The Call button uses your profile's phone number, so it can't have a link.";
    }
    if (cta !== 'CALL' && !url) {
      return 'Add the link your button should open.';
    }
  }
  const type = input.post_type || 'standard';
  if (type === 'event' || type === 'offer') {
    const ev = input.design?.event;
    if (!ev?.title || !ev?.schedule) {
      return `A ${type} needs a title and a start date.`;
    }
  }
  return null;
}
